import { WaitingListServiceClient } from 'fitbeat.api-clients';
import {
  AreaCategory,
  HomeTimeSlot,
  IAvailableStudioTimeSlots,
  ProcessTypes, UserProfile,
  UserWorkoutSession,
  getAllGymAreaCategories,
} from 'fitbeat.gym-core';
import {
  Gym, GymTypes, IDay, TimeSlotEligibility, TimeSlotType,
} from 'fitbeat.models';
import moment from 'moment-timezone';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { translate } from 'react-i18next';
import {
  ScrollView, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import Accordion from 'react-native-collapsible/Accordion';
import { connect } from 'react-redux';

import {
  Button, ButtonSize, ButtonTypes, Icon,
} from '../../components';
import { GymResolver } from '../../services';
import Theme from '../../theme';
import { FormError } from '../../ui';
import { i18n } from '../../utils';
import { getTimezoneFromGymName } from '../../utils/gyms';
import { defaultScale } from '../../utils/scaling';
import {
  getPreviousBookingStep as dispatchGetPreviousBookingStep,
  loadSelectStartTimeScreen as dispatchLoadSelectStartTimeScreen,
  selectDateTimeNextClicked as dispatchSelectDateTimeNextClicked,
  setPendingSlot as dispatchSetPendingSlot,
  setSoloWorkout as dispatchSetSoloWorkout,
  startOnDemandWorkoutPressedAction,
  upgradeStudioPlanPressed as upgradeStudioPlanPressedAction,
  workoutDateTimeSelected as dispatchWorkoutDateTimeSelected,
} from '../actions';
import {
  BookingFlowFooter,
  HomeTimeSlotSelection,
} from '../components';
import AreaCardHeader from '../components/AreaCardHeader';
import { TimeSlotEligibilityMessage } from '../components/TimeSlotEligibilityMessage';
import { areaCategoriesSelector, selectStartTimePrimaryActionLoading } from '../selectors';
import StudioZoneTimeslotCard from './StudioZoneTimeslotCard';
import { styles as sharedStyles } from './Styles';

export interface IProps {
  day: IDay;
  userProfile: UserProfile;
  workoutSession: UserWorkoutSession;
  workoutSessionService: any;
  waitingListServiceClient: WaitingListServiceClient;
  getNextBookingStep: any;
  getPreviousBookingStep: any;
  isSoloWorkout: boolean;
  currentSelectedGymName: string;
  isLoadingBookingSlots: boolean;
  homeSlots: HomeTimeSlot[];
  areaCategories: AreaCategory[];
  bookingSlotsError: string | null;
  pendingSlot: any;
  workoutType: any;
  loadSelectStartTimeScreen: any;
  nextLoading: boolean;
  t(key: string): string;
  setSoloWorkout(isSoloWorkout: boolean): void;
  setPendingSlot(pendingSlot): void;
  selectDateTimeNextClicked(
    waitingListServiceClient, workoutSessionService,
    day, userProfile: UserProfile, gym: Gym,
  ): void;
  workoutDateTimeSelected(date: Date): void;
  upgradeStudioPlanPressed(): void;
  startOnDemandWorkoutPressed(): void;
}

const SelectZoneStartTime: FunctionComponent<IProps> = ({
  t,
  day,
  userProfile,
  workoutSession,
  workoutSessionService,
  getPreviousBookingStep,
  workoutType,
  currentSelectedGymName,
  isSoloWorkout,
  isLoadingBookingSlots,
  homeSlots,
  areaCategories,
  bookingSlotsError,
  pendingSlot,
  loadSelectStartTimeScreen,
  setSoloWorkout,
  setPendingSlot,
  selectDateTimeNextClicked,
  workoutDateTimeSelected,
  waitingListServiceClient,
  upgradeStudioPlanPressed,
  startOnDemandWorkoutPressed,
  nextLoading,
}) => {
  const gym = GymResolver.getByName(currentSelectedGymName);
  const gymTimezone = getTimezoneFromGymName(currentSelectedGymName);
  const [timeSlotEligibility, setTimeSlotEligibility] = useState<TimeSlotEligibility>(TimeSlotEligibility.Eligible);
  const [timeSlotType, setTimeSlotType] = useState<TimeSlotType | null>(null);
  const [activeAccordionIndex, setActiveAccordionIndex] = useState(0);

  useEffect(() => {
    if (gym.type === GymTypes.Indoor) {
      loadSettingAndSlotsStudio();
    }
  }, []);

  const nextClicked = () => {
    selectDateTimeNextClicked(waitingListServiceClient, workoutSessionService, day, userProfile, gym);
  };

  const loadSettingAndSlotsStudio = () => {
    loadSelectStartTimeScreen(workoutSessionService, day, userProfile, gym);
  };

  const getButtonTitle = () => {
    if (timeSlotEligibility === TimeSlotEligibility.SubscriptionUpgradeRequired) {
      return i18n.t('bookings:upgradePlan');
    }

    if (pendingSlot?.bookingType === 'instant') {
      const now = moment().tz(gym.timezone);
      const pendingSlotTime = moment.tz(pendingSlot.startTime, gym.timezone);
      const minutesDiff = pendingSlotTime.diff(now.startOf('minute'), 'minutes');
      if (minutesDiff <= 0) {
        return i18n.t('bookings:startNow');
      }
      return i18n.t('bookings:onDemandBookingStartsIn', { minutes: minutesDiff });
    }

    return i18n.t('bookings:next');
  };

  const handleNextPressed = () => {
    if (pendingSlot?.eligibility === TimeSlotEligibility.SubscriptionUpgradeRequired) {
      return upgradeStudioPlanPressed();
    }
    if (pendingSlot?.bookingType === 'instant') {
      return startOnDemandWorkoutPressed();
    }

    return nextClicked();
  };

  const getAreaSections = (areaGroup: 'circuit' | 'static') => {
    const areas: {}[] = [];

    areaCategories.map((category) => {
      areas.push({
        title: category,
        content: (
          <StudioZoneTimeslotCard
            t={t}
            userProfile={userProfile}
            {...{ setTimeSlotEligibility, setTimeSlotType }}
            areaCategory={category}
            preferredStartTime={day.date}
          />
        ),
      });
    });

    return areas;
  };

  const renderAreaHeader = (section, index, isActive) => (
    <AreaCardHeader
      t={t}
      title={section.title}
      index={index}
      isActive={isActive}
      setActiveAccordionIndex={setActiveAccordionIndex}
    />
  );

  const renderAccordionContent = (section) => (
    <>
      {section.content}
    </>
  );

  const circuitAreaSections = getAreaSections('circuit');

  return (
    <View style={styles.container}>
      {gym.type === GymTypes.Home
        ? (
          <View style={sharedStyles.body}>
            <HomeTimeSlotSelection
              t={t}
              useRadiosSelections={false}
              day={day}
              booking={workoutSession}
              userProfile={userProfile}
              pendingSlot={pendingSlot}
              timeSlots={homeSlots}
              gymTimezone={gymTimezone}
              isSoloWorkout={isSoloWorkout}
              setSoloWorkout={setSoloWorkout}
              setPendingSlot={setPendingSlot}
              workoutDateTimeSelected={workoutDateTimeSelected}
              workoutType={workoutType}
              containerStyle={{ paddingVertical: 0 }}
              isLoadingBookingSlots={isLoadingBookingSlots}
            />
          </View>
        )
        : (
          <ScrollView style={styles.areasContainer}>
            <Accordion
              sections={circuitAreaSections}
              renderHeader={renderAreaHeader}
              renderContent={renderAccordionContent}
              touchableComponent={TouchableOpacity}
              activeSection={activeAccordionIndex}
            />
          </ScrollView>
        )}
      <BookingFlowFooter>
        {bookingSlotsError && <FormError error={new Error(bookingSlotsError)} />}
        <TimeSlotEligibilityMessage
          t={t}
          timeSlotType={timeSlotType}
          userTimeSlotType={userProfile.timeSlotType}
          timeSlotEligibility={timeSlotEligibility}
          gym={gym}
        />
        <View style={styles.footer}>
          <Button
            fontColor={Theme.PRIMARY}
            size={ButtonSize.MEDIUM}
            buttonType={ButtonTypes.OUTLINE}
            title={i18n.t('bookings:back')}
            onPress={() => getPreviousBookingStep()}
            containerStyle={styles.backContainer}
          />
          <Button
            disabled={!pendingSlot}
            size={ButtonSize.MEDIUM}
            fontColor={Theme.WHITE}
            title={getButtonTitle()}
            onPress={() => handleNextPressed()}
            containerStyle={styles.nextContainer}
            isLoading={nextLoading}
          />
        </View>
      </BookingFlowFooter>
    </View>
  );
};

const mapStateToProps = (state): Partial<IProps> => ({
  isSoloWorkout: state.bookings.bookingWizard.isSoloWorkout,
  currentSelectedGymName: state.bookings.bookingWizard.currentSelectedGymName,
  isLoadingBookingSlots: state.bookings.bookingWizard.isLoadingBookingSlots,
  homeSlots: state.bookings.bookingWizard.homeSlots,
  areaCategories: areaCategoriesSelector(state),
  bookingSlotsError: state.bookings.bookingWizard.bookingSlotsError,
  pendingSlot: state.bookings.bookingWizard.pendingSlot,
  workoutSession: state.bookings.bookingWizard.workoutSession,
  workoutType: state.bookings.bookingWizard.workoutType,
  nextLoading: selectStartTimePrimaryActionLoading(state),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  getPreviousBookingStep: () => dispatch(dispatchGetPreviousBookingStep()),
  setSoloWorkout: (isSoloWorkout: boolean) => dispatch(dispatchSetSoloWorkout(isSoloWorkout)),
  setPendingSlot: (pendingSlot) => dispatch(dispatchSetPendingSlot(pendingSlot, undefined)),
  selectDateTimeNextClicked: (
    waitingListServiceClient,
    workoutSessionService,
    day,
    userProfile: UserProfile,
    gym: Gym,
  ) => dispatch(
    dispatchSelectDateTimeNextClicked(waitingListServiceClient, workoutSessionService, day, userProfile, gym),
  ),
  workoutDateTimeSelected: (date) => dispatch(dispatchWorkoutDateTimeSelected(date)),
  loadSelectStartTimeScreen: (
    workoutSessionService,
    day,
    userProfile: UserProfile,
    gym: Gym,
  ) => dispatch(dispatchLoadSelectStartTimeScreen(workoutSessionService, day, userProfile, gym)),
  upgradeStudioPlanPressed: () => dispatch(upgradeStudioPlanPressedAction(ownProps.userProfile)),
  startOnDemandWorkoutPressed: () => dispatch(startOnDemandWorkoutPressedAction(ownProps.userProfile)),
});

export const SelectZoneStartTimeStep = connect(mapStateToProps, mapDispatchToProps)(
  translate(['bookings', 'common'], { wait: true })(SelectZoneStartTime),
);

SelectZoneStartTimeStep.getTitle = ({ t, closeModal, processType }) => (
  <View style={styles.header}>
    <Text style={styles.title}>{t('selectWorkoutTime')}</Text>
    {processType !== ProcessTypes.DemoStudioBooking && (
      <TouchableOpacity
        hitSlop={{
          bottom: defaultScale(20), top: defaultScale(20), left: defaultScale(20), right: defaultScale(20),
        }}
        onPress={closeModal}
      >
        <Icon
          name='md-close'
          color={Theme.GREY}
          size={defaultScale(30)}
        />
      </TouchableOpacity>
    )}
  </View>
);

const styles = StyleSheet.create({
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: defaultScale(15),
  },
  title: {
    ...Theme.textStyle.heading,
  },
  container: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  areasContainer: {
    backgroundColor: Theme.GREY_FIGMA,
    height: '100%',
  },
  areaGroupHeading: {
    ...Theme.textStyle.heading,
    color: Theme.GREY_DARK,
    marginTop: defaultScale(25),
    marginLeft: defaultScale(15),
    marginBottom: defaultScale(5),
  },
  footer: {
    flexDirection: 'row',
    paddingHorizontal: defaultScale(15),
    paddingBottom: defaultScale(15),
  },
  backContainer: {
    flex: 0.3,
  },
  nextContainer: {
    flex: 0.7,
    marginLeft: defaultScale(5),
  },
});
